// @ts-nocheck
/*
 * Port of views/dashboard/module/TtsOperatorAclModal.js — byte-identical.
 * v1 imports getApiCall from `service/getApiCall` (not httpCall) — matches
 * the v2 file at src/service/getApiCall.ts. Hardcoded "/v2/tts-languages"
 * URL preserved from v1.
 */
import React, { useEffect, useState } from 'react';
import {
  TTS_OPERATOR_SARVAM,
  TTS_OPERATOR_OMNI,
  DEFAULT_TTS_OPERATOR,
  normalizeTtsOperatorForSave,
} from '@/utilities/ttsAcl';
import { AclSettingsModal, AclModalSection, FaMicrophone } from '@/features/acl-settings/aclSettingsUi';
import { getApiCall } from '@/service/getApiCall';
import { FaGlobe } from 'react-icons/fa';

const TTS_OPTIONS = [
  { value: TTS_OPERATOR_SARVAM, label: 'SARVAM', description: 'Use Sarvam as the TTS operator' },
  { value: TTS_OPERATOR_OMNI, label: 'OMNI VOICE', description: 'Use Omni Voice as the TTS operator' },
];

const TtsOperatorAclModal = ({ show, onHide, initialOperator, initialLanguages = [], onSave }) => {
  const [selected, setSelected] = useState(DEFAULT_TTS_OPERATOR);
  const [selectedLangs, setSelectedLangs] = useState([]);
  const [allLanguages, setAllLanguages] = useState([]);
  const [langsLoading, setLangsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setSelected(
        initialOperator && [TTS_OPERATOR_SARVAM, TTS_OPERATOR_OMNI].includes(initialOperator)
          ? initialOperator
          : DEFAULT_TTS_OPERATOR,
      );
      setSelectedLangs(Array.isArray(initialLanguages) ? initialLanguages : []);

      if (allLanguages.length === 0) {
        setLangsLoading(true);
        getApiCall('/v2/tts-languages')
          .then((r) => { if (Array.isArray(r)) setAllLanguages(r); })
          .finally(() => setLangsLoading(false));
      }
    }
  }, [show, initialOperator, initialLanguages]);

  const toggleLanguage = (code) => {
    setSelectedLangs((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const handleSelectAll = () => setSelectedLangs(allLanguages.map((l) => l.code));
  const handleClearAll = () => setSelectedLangs([]);

  const handleReset = () => {
    setSelected(DEFAULT_TTS_OPERATOR);
    setSelectedLangs([]);
  };

  const handleSave = () => {
    onSave(normalizeTtsOperatorForSave(selected), selectedLangs);
    onHide();
  };

  return (
    <AclSettingsModal
      show={show}
      onHide={onHide}
      title="TTS — Operator & Languages"
      icon={FaMicrophone}
      description="Select the TTS operator and the languages users with this ACL can access."
      onSave={handleSave}
      onReset={handleReset}
      size="md"
    >
      <AclModalSection
        icon={FaMicrophone}
        title="TTS Operator"
        subtitle="Only one operator can be active at a time."
      >
        <div className="d-flex flex-column gap-2 mt-2">
          {TTS_OPTIONS.map((opt) => (
            <div key={opt.value} className="form-check acl-modal-check acl-modal-check--block mb-0">
              <input
                type="checkbox"
                id={`tts-operator-${opt.value}`}
                className="form-check-input"
                checked={selected === opt.value}
                onChange={() => setSelected(opt.value)}
              />
              <label htmlFor={`tts-operator-${opt.value}`} className="form-check-label">
                <strong>{opt.label}</strong>
                <span className="d-block acl-modal-check-desc">{opt.description}</span>
              </label>
            </div>
          ))}
        </div>
      </AclModalSection>

      <AclModalSection
        icon={FaGlobe}
        title="Allowed Languages"
        subtitle={
          selectedLangs.length === 0
            ? 'No restriction — all languages allowed.'
            : `${selectedLangs.length} language${selectedLangs.length !== 1 ? 's' : ''} selected.`
        }
      >
        {langsLoading ? (
          <div style={{ padding: '0.5rem 0', color: '#94a3b8', fontSize: '0.82rem' }}>Loading languages…</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', marginTop: '0.5rem' }}>
              <button
                type="button"
                onClick={handleSelectAll}
                style={{
                  fontSize: '0.75rem',
                  padding: '0.2rem 0.7rem',
                  borderRadius: 6,
                  border: '1px solid #e2e8f0',
                  background: '#f8fafc',
                  cursor: 'pointer',
                  color: '#475569',
                }}
              >
                Select All
              </button>
              <button
                type="button"
                onClick={handleClearAll}
                style={{
                  fontSize: '0.75rem',
                  padding: '0.2rem 0.7rem',
                  borderRadius: 6,
                  border: '1px solid #e2e8f0',
                  background: '#f8fafc',
                  cursor: 'pointer',
                  color: '#475569',
                }}
              >
                Clear All
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem', maxHeight: 220, overflowY: 'auto' }}>
              {allLanguages.map((lang) => (
                <div key={lang.code} className="form-check acl-modal-check mb-0">
                  <input
                    type="checkbox"
                    id={`tts-lang-${lang.code}`}
                    className="form-check-input"
                    checked={selectedLangs.includes(lang.code)}
                    onChange={() => toggleLanguage(lang.code)}
                  />
                  <label
                    htmlFor={`tts-lang-${lang.code}`}
                    className="form-check-label"
                    style={{ fontSize: '0.82rem' }}
                  >
                    {lang.language}
                  </label>
                </div>
              ))}
            </div>
          </>
        )}
      </AclModalSection>
    </AclSettingsModal>
  );
};

export default TtsOperatorAclModal;
